import { Link } from "react-router";
import { ToggleSwitch } from "flowbite-react";
import { Info } from "lucide-react";
import { toast } from "sonner";
import { PREDICTIONS_MATCHES_PATH } from "@/features/predictions/constants/paths";
import { usePreferencesMutation } from "@/features/predictions/hooks/usePreferencesMutation";
import type { MyPronosticsPreferences } from "@/features/predictions/types/preferences.type";

type PreferenceKey = "emailDailyDigest" | "pushMatchReminder";

type Preference = {
  key: PreferenceKey
  title: string
  subtitle: string
};

const PREFERENCES: readonly Preference[] = [
  {
    key: "emailDailyDigest",
    title: "Email récap quotidien",
    subtitle: "Un mail chaque matin à 10h30 avec les résultats, les matchs à venir et ton classement.",
  },
  {
    key: "pushMatchReminder",
    title: "Rappel push avant les matchs",
    subtitle: "Une notif 2h avant chaque match auquel tu n'as pas encore pronostiqué.",
  },
] as const;

type Props = {
  preferences: MyPronosticsPreferences
  disabled?: boolean
};

export const NotificationsPreferencesControls = ({ preferences, disabled = false }: Props) => {
  const mutation = usePreferencesMutation();
  const isMutedByEngagement = preferences.isMuted;
  const isReadOnly = disabled || isMutedByEngagement;

  const handleToggle = (key: PreferenceKey) => (value: boolean) => {
    mutation.mutate(
      { [key]: value },
      {
        onSuccess: () => {
          toast.success("Préférences enregistrées");
        },
        onError: () => {
          toast.error("Impossible d'enregistrer la préférence");
        },
      },
    );
  };

  const getToggleValue = (key: PreferenceKey): boolean => {
    if (isMutedByEngagement) return false;
    return preferences[key];
  };

  return (
    <>
      {isMutedByEngagement && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <Info size={18} aria-hidden className="mt-0.5 shrink-0" />
          <p>
            Tu n&apos;as plus pronostiqué les 4 derniers matchs, on a coupé tes notifications.
            {" "}
            <Link
              to={PREDICTIONS_MATCHES_PATH}
              className="font-semibold underline underline-offset-2 hover:no-underline"
            >
              Refais un pronostic
            </Link>
            {" "}
            pour les réactiver.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {PREFERENCES.map((preference) => (
          <li
            key={preference.key}
            className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/60"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {preference.title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {preference.subtitle}
              </p>
            </div>
            <ToggleSwitch
              checked={getToggleValue(preference.key)}
              onChange={handleToggle(preference.key)}
              disabled={isReadOnly || mutation.isPending}
              label=""
            />
          </li>
        ))}
      </ul>
    </>
  );
};

NotificationsPreferencesControls.displayName = "NotificationsPreferencesControls";
